import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../css/desktop/DesktopSidebar.css';
import { useLang, LANGUAGES } from '../../i18n';
import { useGeo, CURRENCIES } from '../../geo';

const PATHS = {
  home:         'M3 9.5L12 3l9 6.5V21a1 1 0 01-1 1H5a1 1 0 01-1-1zM9 22V12h6v10',
  language:     'M12 3a9 9 0 100 18 9 9 0 000-18zM3 12h18M12 3c2.5 2.5 2.5 16 0 18M12 3c-2.5 2.5-2.5 16 0 18',
  currency:     'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  sports:       'M12 3a9 9 0 100 18 9 9 0 000-18zm0 3l3 2.2-1.1 3.4H10.1L9 8.2zM4.2 10.4l2.7.5M19.8 10.4l-2.7.5M7.2 18l1.6-2.9M16.8 18l-1.6-2.9',
  live:         'M12 4a8 8 0 108 8M12 8v4l2.5 1.6M9.5 3h5M12 3v1.8M18 5l1.5-1.5',
  search:       'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35',
  bets:         'M5 4.5h11a1 1 0 011 1V20l-2-1.3L13 20l-2-1.3L9 20l-2-1.3L5 20zM8 8h7M8 11h7M8 14h4',
  transactions: 'M7 7h11l-2.5-2.5M17 17H6l2.5 2.5',
  history:      'M12 3a9 9 0 109 9M12 3a9 9 0 00-9 9M3 5v3h3M12 7v5l3 2',
  favourites:   'M12 3.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.6l5.8-.8z',
  kyc:          'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  terms:        'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 15h4',
  privacy:      'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  responsible:  'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  logout:       'M14 4H6a1 1 0 00-1 1v14a1 1 0 001 1h8M14 12H10M14 12l-2.5-2.5M14 12l-2.5 2.5M19 12h-5',
};

const SPORTS = [
  { key: 'football',         img: 'sport-football',          path: '/football' },
  { key: 'basketball',       img: 'sport-basketball',        path: '/basketball' },
  { key: 'tennis',           img: 'sport-tennis',            path: '/tennis' },
  { key: 'iceHockey',        img: null,                      path: '/ice-hockey' },
  { key: 'tableTennis',      img: 'sport-tabletennis',       path: '/table-tennis' },
  { key: 'volleyball',       img: 'sport-volleyball',        path: '/volleyball' },
  { key: 'rugby',            img: 'sport-rugby',             path: '/rugby' },
  { key: 'americanFootball', img: 'sport-american-football', path: '/american-football' },
  { key: 'handball',         img: 'sport-handball',          path: '/handball' },
  { key: 'mma',              img: 'sport-mma',               path: '/mma' },
];

function SbIcon({ name }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      className="dsk-sb-icon-svg"
    >
      <path
        d={PATHS[name] || PATHS.home}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NavItem({ icon, label, path, active, onClick }) {
  return (
    <button
      className={'dsk-sb-item' + (active ? ' is-active' : '')}
      onClick={onClick}
    >
      <SbIcon name={icon} />
      <span>{label}</span>
    </button>
  );
}

function LangPickerSidebar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  return (
    <div className="dsk-sb-lang">
      <button className="dsk-sb-item dsk-sb-lang-trigger" onClick={() => setOpen(v => !v)}>
        <SbIcon name="language" />
        <span className="dsk-sb-lang-label">{t('menu.language')}</span>
        <span className="dsk-sb-lang-current">{current.flag} {current.label}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={'dsk-sb-lang-chev' + (open ? ' open' : '')}>
          <path d="M2 3.5l3 3 3-3" stroke="#5a5972" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="dsk-sb-lang-grid">
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              className={'dsk-sb-lang-item' + (l.code === lang ? ' active' : '')}
              onClick={() => { setLang(l.code); setOpen(false); }}
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SportsPickerSidebar() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isActive = SPORTS.some(s => location.pathname === s.path);

  return (
    <div className="dsk-sb-lang">
      <button className={'dsk-sb-item dsk-sb-lang-trigger' + (isActive ? ' is-active' : '')} onClick={() => setOpen(v => !v)}>
        <SbIcon name="sports" />
        <span className="dsk-sb-lang-label">{t('nav.sports')}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={'dsk-sb-lang-chev' + (open ? ' open' : '')}>
          <path d="M2 3.5l3 3 3-3" stroke="#5a5972" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="dsk-sports-grid">
          {SPORTS.map(s => (
            <button key={s.key} className={'dsk-sports-item' + (location.pathname === s.path ? ' active' : '')} onClick={() => { setOpen(false); navigate(s.path); }}>
              {s.img
                ? <i style={{ display: 'inline-block', flex: 'none', width: 14, height: 14, backgroundColor: 'currentColor', mask: `url('/img/${s.img}.svg') center center / contain no-repeat`, WebkitMask: `url('/img/${s.img}.svg') center center / contain no-repeat` }} />
                : <svg viewBox="0 0 12 8.571" width="16" height="11" fill="currentColor" style={{ flex: 'none' }}><path d="M12 4.286v1.071C12 7.132 9.314 8.571 6 8.571 2.745 8.571.096 7.183.003 5.452L0 5.357V4.286C0 6.061 2.686 7.5 6 7.5c3.314 0 6-1.439 6-2.214zM6 0c3.314 0 6 1.439 6 3.214C12 4.99 9.314 6.429 6 6.429 2.686 6.429 0 4.989 0 3.214 0 1.439 2.686 0 6 0z" /></svg>
              }
              <span>{t(`home.sports.${s.key}`)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CurrencyPickerSidebar() {
  const { currency, setCurrency, prices } = useGeo();
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <div className="dsk-sb-lang">
      <button className="dsk-sb-item dsk-sb-lang-trigger" onClick={() => setOpen(v => !v)}>
        <SbIcon name="currency" />
        <span className="dsk-sb-lang-label">{t('menu.currency')}</span>
        <span className="dsk-sb-lang-current">{prices.symbol} {currency}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={'dsk-sb-lang-chev' + (open ? ' open' : '')}>
          <path d="M2 3.5l3 3 3-3" stroke="#5a5972" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="dsk-sb-lang-grid">
          {CURRENCIES.map(c => (
            <button
              key={c.code}
              className={'dsk-sb-lang-item' + (c.code === currency ? ' active' : '')}
              onClick={() => { setCurrency(c.code); setOpen(false); }}
            >
              <span>{c.symbol}</span>
              <span>{c.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DesktopSidebar({ activeTab }) {
  const { t } = useLang();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });

  useEffect(() => {
    const sync = () => {
      try { setUser(JSON.parse(localStorage.getItem('user'))); } catch { setUser(null); }
    };
    window.addEventListener('userUpdated', sync);
    window.addEventListener('storage',     sync);
    return () => {
      window.removeEventListener('userUpdated', sync);
      window.removeEventListener('storage',     sync);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    window.dispatchEvent(new Event('userUpdated'));
    navigate('/');
  };

  const geo         = useGeo();
  const { symbol, rate } = geo.prices;
  const balance     = ((user?.solde_actuel != null ? parseFloat(user.solde_actuel) : 0) * rate).toFixed(2);
  const displayName = user?.username || user?.prenom || user?.nom || 'Utilisateur';
  const at          = (p) => location.pathname === p;

  return (
    <aside className="dsk-sb">

      {/* ── Logo ── */}
      <div className="dsk-sb-logo" onClick={() => navigate('/')}>
        <img className="dsk-sb-logo-img" src="/logo.png" alt="Roxs Sports" />
      </div>

      <div className="dsk-sb-scroll">

        {/* ── Utilisateur connecté ── */}
        {user ? (
          <div className="dsk-sb-user">
            <button
              className="dsk-sb-profile-card"
              onClick={() => navigate('/profile/edit')}
            >
              <span className="dsk-sb-avatar" />
              <span className="dsk-sb-user-text">
                <span className="dsk-sb-username">{displayName}</span>
                <span className="dsk-sb-balance">{balance} {symbol}</span>
              </span>
            </button>
            <div className="dsk-sb-funds">
              <button className="dsk-sb-deposit"  onClick={() => navigate('/deposit')}>{t('menu.deposit')}</button>
              <button className="dsk-sb-withdraw" onClick={() => navigate('/withdrawal')}>{t('menu.withdraw')}</button>
            </div>
          </div>
        ) : (
          <div className="dsk-sb-auth">
            <button className="dsk-sb-btn-login"    onClick={() => navigate('/auth')}>{t('menu.signIn')}</button>
            <button className="dsk-sb-btn-register" onClick={() => navigate('/auth?tab=signup')}>{t('menu.createAccount')}</button>
          </div>
        )}

        {/* ── Paris ── */}
        <div className="dsk-sb-section">
          <div className="dsk-sb-section-title">{t('menu.sectionBetting')}</div>
          <NavItem icon="home"   label={t('nav.home')}          path="/"       active={at('/')}       onClick={() => navigate('/')} />
          <SportsPickerSidebar />
          <NavItem icon="live"   label={t('home.status.live')}  path="/live-betting" active={false}         onClick={() => navigate('/live-betting')} />
          <NavItem icon="search" label={t('common.search')}     path={null}    active={false}         onClick={() => window.dispatchEvent(new Event('openSearch'))} />
        </div>

        {/* ── Mon compte (connecté seulement) ── */}
        {user && (
          <div className="dsk-sb-section">
            <div className="dsk-sb-section-title">{t('menu.sectionAccount')}</div>
            <NavItem icon="bets"         label={t('menu.myBets')}          path="/mybets"       active={at('/mybets')}       onClick={() => navigate('/mybets')} />
            <NavItem icon="transactions" label={t('menu.transactions')}    path="/transactions" active={at('/transactions')} onClick={() => navigate('/transactions')} />
            <NavItem icon="favourites"   label={t('menu.favorites')}       path="/favoris"      active={at('/favoris')}      onClick={() => navigate('/favoris')} />
          </div>
        )}


        {/* ── Langue ── */}
        <div className="dsk-sb-section">
          <div className="dsk-sb-section-title">{t('menu.sectionSettings')}</div>
          <LangPickerSidebar />
          <CurrencyPickerSidebar />
        </div>

        {/* ── Déconnexion ── */}
        {user && (
          <div className="dsk-sb-section dsk-sb-bottom">
            <button className="dsk-sb-item dsk-sb-logout" onClick={handleLogout}>
              <SbIcon name="logout" />
              <span>{t('menu.logout')}</span>
            </button>
          </div>
        )}

      </div>
    </aside>
  );
}
